import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppSelector } from "hooks/redux";
import { StatusCode } from "api/protocols";
import { PutBookingReminderAction } from "api/rpc/2024-04/guest/teesheet";
import Spin from "components/spin/spin";
import FeatureIcon from "components/icon/FeatureIcon";
import "pages/guest/Customer/ClientPortal/WaitList/cancelWaitTime.scss";

interface IState {
  success: boolean | undefined;
  message: string;
}

function titleForAction(action: string | undefined, success: boolean | undefined): string {
  if (success === undefined) {
    return "";
  }
  if (!success) {
    return "Error";
  }

  switch (action) {
    case "confirm":
      return "Attendance Confirmed";
    case "cancel-self":
    case "cancel_self":
      return "Spot Cancelled";
    case "cancel-booking":
    case "cancel_booking":
      return "Booking Cancelled";
    default:
      return "Success";
  }
}

function fallbackMessage(action: string | undefined, success: boolean): string {
  if (!success) {
    return "There was an error completing this booking reminder action.";
  }

  switch (action) {
    case "confirm":
      return "Your attendance has been confirmed. See you on the course!";
    case "cancel-self":
    case "cancel_self":
      return "Your spot has been cancelled successfully.";
    case "cancel-booking":
    case "cancel_booking":
      return "The booking has been cancelled successfully.";
    default:
      return "Your request was completed successfully.";
  }
}

export default function BookingReminderAction() {
  const { authStore } = useAppSelector(store => store);
  const { token, action } = useParams<{ token: string; action: string }>();
  const [state, setState] = useState<IState>({
    success: undefined,
    message: "",
  });

  useEffect(() => {
    if (!authStore.loaded) {
      return;
    }

    void handleAction();
  }, [authStore.loaded, token, action]);

  async function handleAction() {
    if (!token || !action) {
      setState({
        success: false,
        message: "This reminder link is invalid.",
      });
      return;
    }

    const res = await PutBookingReminderAction({ token, action }, true);
    const success = res.status === StatusCode.OK;
    let apiMessage = "";
    if (typeof res.data === "string") {
      apiMessage = res.data;
    } else if (res.data && typeof res.data === "object" && "message" in res.data) {
      apiMessage = String((res.data as { message?: string }).message ?? "");
    } else if (typeof res.message === "string") {
      apiMessage = res.message;
    }

    setState({
      success,
      message: apiMessage || fallbackMessage(action, success),
    });
  }

  return (
    <div className="waitlist-cancel-parent-container">
      <div className="waitlist-cancel-inner-container">
        {state.success === undefined ? (
          <div style={{ height: "50px", width: "50px" }}>
            <Spin />
          </div>
        ) : (
          <div className="waitlist-group">
            {state.success ? (
              <FeatureIcon type="success" theme="light-circle-outline" size="large" icon="check" />
            ) : (
              <FeatureIcon type="error" theme="light-circle-outline" size="large" icon="exclamation-triangle" />
            )}
            <p className="cancel-title">{titleForAction(action, state.success)}</p>
            <p className="cancel-subtext">{state.message}</p>
          </div>
        )}
      </div>
    </div>
  );
}
